import { WhmStack } from "@securitize/reactjs-whm";
import { SettingsLoadingPage } from "../loading/SettingsLoadingPage";
import { SettingsSecurityCard } from "./SettingsSecurityCard";
import { useGetSettingsQuery } from "../../../../data/useProfile";
import { SettingsInternalProps } from "./SettingsInternalProps";
import SettingsEmailField from "./SettingsEmailField";
import SettingsLanguageField from "./SettingsLanguageField";
import SettingsPhoneNumberField from "./SettingsPhoneNumberField";
import SettingsDownloadInfoCard from "./SettingsDownloadInfoCard";
import { SettingsDevicesCard } from "./SettingsDevicesCard";
import { ErrorPage } from "../../../shared/ErrorPage";
import SettingsDeleteCard from "./SettingsDeleteCard";
import React from "react";
import useUserInfo from "../../../../hooks/useUserInfo";
import { InvestorTypes } from "../../../../data/types/investors";
import SettingsTitles from "./SettingsTitles";
import { ProfilePermissionTypes } from "../../../../data/dtos/profile-config.dto";
import SettingsRemoveAccessCard from "./SettingsRemoveAccessCard";
import SettingsAccountIDCard from "./SettingsAccountIDCard";

export default function SettingsMobile({
  isLoading,
  mfa,
  changePassword,
  language,
  onChangeLanguage,
  permission,
  sessions,
  displayableId,
}: SettingsInternalProps) {
  const { investorType, entityName } = useUserInfo();
  const settingsQuery = useGetSettingsQuery();

  const isEntity = investorType === InvestorTypes.ENTITY;
  const isReadOnly = permission === ProfilePermissionTypes.READ_ONLY;
  const shouldShowRemoveAccess = isEntity && isReadOnly;

  // Main UI
  if (settingsQuery.isPending || isLoading || sessions.isLoading) {
    return <SettingsLoadingPage />;
  }

  if (settingsQuery.isError || sessions.isError) {
    return (
      <ErrorPage
        errorCode={
          settingsQuery.isError
            ? settingsQuery.error.correlationId
            : sessions.correlationId
        }
        onRefresh={
          settingsQuery.isError ? settingsQuery.refetch : sessions.onRefresh
        }
      />
    );
  }

  return (
    <WhmStack gap="20px">
      {isEntity && (
        <WhmStack gap="20px">
          {settingsQuery.data.hasEntityAccount && (
            <SettingsTitles
              investorType={InvestorTypes.ENTITY}
              entityName={entityName}
            />
          )}

          <SettingsDownloadInfoCard permission={permission} />
          {shouldShowRemoveAccess ? (
            <SettingsRemoveAccessCard />
          ) : (
            <SettingsDeleteCard permission={permission} />
          )}
        </WhmStack>
      )}

      {settingsQuery.data.hasEntityAccount && (
        <SettingsTitles
          investorType={InvestorTypes.INDIVIDUAL}
          fullName={settingsQuery.data.name}
        />
      )}

      <SettingsEmailField email={settingsQuery.data.email} />
      <SettingsPhoneNumberField phone={settingsQuery.data.phone} />
      <SettingsSecurityCard
        onMfa={mfa.onMfa}
        hasMfa={mfa.hasMfa}
        changePassword={changePassword}
      />
      <SettingsAccountIDCard displayableId={String(displayableId)} />
      <SettingsDevicesCard
        activeUserSession={sessions.activeUserSession}
        onDeleteSession={sessions.onDeleteSession}
        userSessions={sessions.userSessions}
      />
      <SettingsLanguageField
        currentLanguage={language}
        onChangeLanguage={onChangeLanguage}
      />
      {!isEntity && <SettingsDownloadInfoCard permission={permission} />}
      {!isEntity && <SettingsDeleteCard permission={permission} />}
    </WhmStack>
  );
}
